import { RefObject, useEffect, useRef, useState } from "react";

import { localFeatureFlagsAtom } from "@impero-com/common/utils/flipper";
import { useAtomValue } from "jotai";

import { ENVIRONMENT } from "../../env";

import { hidden, watermark } from "./gitInfoWatermark.css";

export const SHORT_COMMIT_LENGTH = 7;

export function GitInfoWatermark() {
    const flags = useAtomValue(localFeatureFlagsAtom);
    const ref = useRef<HTMLDivElement>(null);
    const isCursorOver = useIsCursorOver(ref, flags.gitInfoWatermark);
    if (!flags.gitInfoWatermark) {
        return null;
    }

    const className = isCursorOver ? `${watermark} ${hidden}` : watermark;

    return (
        <div ref={ref} className={className}>
            <div>{ENVIRONMENT.GIT_BRANCH ?? "Unknown"}</div>
            <div>
                {ENVIRONMENT.GIT_COMMIT?.slice(0, SHORT_COMMIT_LENGTH) ??
                    "Unknown"}
            </div>
        </div>
    );
}

// Tracks whether the cursor is inside the element's bounding box via a global
// mousemove listener. Used in place of a CSS :hover, which would require
// pointer-events: auto and break the watermark's click-through behaviour.
function useIsCursorOver(ref: RefObject<HTMLElement | null>, enabled: boolean) {
    const [isCursorOver, setIsCursorOver] = useState(false);

    useEffect(() => {
        if (!enabled) {
            return;
        }
        const onMove = (e: MouseEvent) => {
            const el = ref.current;
            if (!el) {
                return;
            }
            const r = el.getBoundingClientRect();
            const inside =
                e.clientX >= r.left &&
                e.clientX <= r.right &&
                e.clientY >= r.top &&
                e.clientY <= r.bottom;
            setIsCursorOver(inside);
        };
        window.addEventListener("mousemove", onMove);
        return () => window.removeEventListener("mousemove", onMove);
    }, [enabled, ref]);

    return isCursorOver;
}
